import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import TypeAhead from '../TypeAhead';
import type { CustomDrawerProps } from './TablemaintPage';
import { LocationMapField, type LocationMapValue } from '../LocationMapField';
import { isDeletedFlag } from './fields';
import { getRails } from '../../services/rails.service';
import { updateEdiLocation } from '../../services/edilocations.service';
import { AUTO_ACCEPT_CAUTION } from '../../config/dataTables.registry';

/**
 * Bespoke edit drawer for EDI Locations (cms.edi_locations). The partner-supplied
 * address block (EDI Handle / Name / Partner Code / address) is read-only; the page
 * only lets users set the four editable fields:
 *   - Location Map — map to a CMS location via the reusable <LocationMapField>
 *     (search existing, or Add New with dedup/undelete). Stores locationcode.
 *   - Terminal (rail) — a cms.rails terminal (same source as Pro Detail's terminal).
 *   - Auto Accept / Deleted — Y/N.
 * Saves via /edi-locations/{recno}. Replaces V1 editedi_locations.
 */
const TABLE = 'edi-locations';
const s = (v: any): string => (v == null ? '' : String(v));

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {(children == null || children === '') ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

const EdiLocationsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;

  const initialMap = useMemo<LocationMapValue>(() => ({
    recno: Number(row?.locationcode) || 0,
    name: s(row?.lname),
    city: s(row?.lcity),
    state: s(row?.lstate),
  }), [row]);

  const [mapVal, setMapVal] = useState<LocationMapValue>(initialMap);
  const [rail, setRail] = useState(s(row?.rail));
  const [autoaccept, setAutoaccept] = useState(s(row?.autoaccept) === 'Y');
  const [formDeleted, setFormDeleted] = useState(s(row?.deleted) === 'Y');
  const [railOptions, setRailOptions] = useState<{ value: string; label: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    getRails()
      .then((rs) => { if (alive) setRailOptions(rs.map((r) => ({ value: r.terminal, label: r.terminal }))); })
      .catch(() => { /* leave empty — the current value is still shown below */ });
    return () => { alive = false; };
  }, []);

  // Keep the current stored rail selectable even if it's not in the active rails list.
  const railOpts = useMemo(() => {
    const base = [{ value: '', label: '— None —' }, ...railOptions];
    if (rail && !base.some((o) => o.value === rail)) return [...base, { value: rail, label: rail }];
    return base;
  }, [railOptions, rail]);

  const dirty = useMemo(
    () =>
      mapVal.recno !== (Number(row?.locationcode) || 0) ||
      rail !== s(row?.rail) ||
      autoaccept !== (s(row?.autoaccept) === 'Y') ||
      formDeleted !== (s(row?.deleted) === 'Y'),
    [mapVal, rail, autoaccept, formDeleted, row],
  );

  const handleSave = useCallback(async () => {
    if (!dirty) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await updateEdiLocation(recno, {
        locationcode: mapVal.recno,
        rail,
        autoaccept: autoaccept ? 'Y' : 'N',
        deleted: formDeleted ? 'Y' : 'N',
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to update EDI location.');
      setIsSaving(false);
    }
  }, [dirty, recno, mapVal, rail, autoaccept, formDeleted, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
      </button>
    </div>
  );

  const title = `EDI Location — ${s(row?.name) || `#${recno}`}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="46rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!!recno && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}

        {/* Partner-supplied data — always read-only. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="EDI Handle">{s(row?.edihandle)}</Field>
          <Field label="Name">{s(row?.name)}</Field>
          <Field label="Partner Code">{s(row?.partnercode)}</Field>
          <Field label="Address">{s(row?.address1)}</Field>
          <Field label="Address 2">{s(row?.address2)}</Field>
          <Field label="City">{s(row?.city)}</Field>
          <Field label="State">{s(row?.state)}</Field>
          <Field label="Zip">{s(row?.zip)}</Field>
          <Field label="Country">{s(row?.country)}</Field>
        </div>

        {/* Editable: Location Map + Terminal + Auto Accept + Deleted. */}
        <div className="pt-1">
          <LocationMapField value={mapVal} onChange={setMapVal} disabled={isSaving} />
          {/* The mapped CMS location was soft-deleted since this EDI location was
              mapped. The picker searches ACTIVE locations only, so without this the
              stale mapping looks healthy. Only while still on the original target —
              once the user picks a new location the note is no longer true. */}
          {isDeletedFlag(row?.locationdeleted) && mapVal.recno > 0
            && mapVal.recno === (Number(row?.locationcode) || 0) && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              This location has been deleted.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-label mb-1">Terminal</label>
          <TypeAhead
            options={railOpts}
            value={rail}
            onChange={(v) => setRail((v as string) || '')}
            onClear={() => setRail('')}
            placeholder="Select terminal…"
            disabled={isSaving}
            showSearch
          />
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          <label className="flex items-start gap-3 cursor-pointer select-none p-3">
            <input type="checkbox" checked={autoaccept} disabled={isSaving}
              onChange={(e) => setAutoaccept(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span>
              <span className="text-sm font-medium text-label">Auto Accept</span>
              {/* Commercial commitment, not a display setting — the same wording as the
                  caution on the EDI Locations list. */}
              <span className="block text-xs text-amber-700 dark:text-amber-400">
                {AUTO_ACCEPT_CAUTION}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer select-none p-3">
            <input type="checkbox" checked={formDeleted} disabled={isSaving}
              onChange={(e) => setFormDeleted(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span>
              <span className="text-sm font-medium text-label">Deleted</span>
              <span className="block text-xs text-muted">
                Hides this EDI location from the list. Uncheck and save to restore it.
              </span>
            </span>
          </label>
        </div>
      </div>
    </Drawer>
  );
};

export default EdiLocationsEditDrawer;
